import { defineStore } from 'pinia';

import { useAppStore } from './app';

import * as constants from '../constants';
import * as Shopify from '../helpers/shopify';

// eslint-disable-next-line import/prefer-default-export
export const useCustomizerStore = defineStore('customizer', {
    id: 'customizer',

    state: () => ({
        // Is the widget currently doing something.
        globalBusy: false,

        // Is the widget self-destructing.
        selfDestruct: false,

        // Widget configuration options.
        options: {},

        // Current rendered image.
        currentImage: '',

        // Currently selected controller.
        viewController: null,

        // Component prices.
        prices: null,

        // Currently configured price.
        statePrice: null,

        // Current outstanding personalization timer.
        personalizationTimer: null,

        personalizationTimerId: 1,
    }),

    getters: {
    },

    actions: {
        /**
         * Save options.
         */
        setOptions(options) {
            this.options = options;
        },

        /**
         * Closes current instance.
         */
        globalCancel() {
            this.options.onCancel();

            this.selfDestruct = true;
        },

        /**
         * Start loading products.
         */
        load(options) {
            if (options) {
                Object.assign(this.options, options);
            }

            if (this.options.recipe) {
                const url = this.options.recipe.replace(/[^0-9a-zA-Z-]/g, '');

                fetch(`//cz.drrv.co/recipe/${url}.json`).then((response) => response.json()).then((recipeJson) => {
                    this.loadCustomizeFlow({
                        startWithProducts: this.options.startWithProducts,
                        recipe: recipeJson,
                    });
                });
            } else {
                this.loadCustomizeFlow({
                    startWithProducts: this.options.startWithProducts,
                });
            }
        },

        /**
         * Calculate default controller options.
         */
        defaultControllerOptions() {
            return {
                apiKey: this.options.apiKey,
                products: this.options.products,
                preview: this.options.preview,
                site: this.options.site,
                version: this.options.version,
                locale: this.options.locale,
                disableCaching: this.options.disableCaching,
            };
        },

        /**
         * Loads prices for components mentioned in the blueprint.
         */
        getPrices() {
            return new Promise((resolve) => {
                resolve();
            });
        },

        /**
         * Retrieve a component price.
         */
        // eslint-disable-next-line no-unused-vars
        getPrice() {
            return 0;
        },

        /**
         * Load controller.
         */
        loadController(controller) {
            controller.on('setReactive', (v) => {
                // Vue 3 doesn't need Vue.set anymore.
                v.target[v.key] = v.value;
                return true;
            });

            controller.on('initializeAvailability', (o) => this.getPrices({
                controller,
                blueprint: o.blueprint,
            }));

            controller.on('afterInitializeState', () => {
            });

            controller.on('isAvailable', () => true);

            controller.on('getPrice', (component) => this.getPrice({
                component,
            }));

            controller.on('stateChanged', () => {
                this.updateStatePrice(controller);
            });

            controller.load();
        },

        /**
         * Updates current state price.
         */
        updateStatePrice(controller) {
            const customizationCode = this.calculateCustomizationCode(controller);

            // eslint-disable-next-line
            console.log(`Calculate price for ${customizationCode}`);

            Promise.all([
                controller.mapValue('oe-shopify-product', customizationCode),
                controller.mapValue('oe-shopify-variant', customizationCode),
            ]).then(([product, variant]) => {
                if (product?.mapped && variant?.mapped) {
                    // eslint-disable-next-line
                    console.log(`Use product ${product.mapped} and variant ${variant.mapped}`);

                    Shopify.getProduct(controller.blueprint, {
                        product: product.mapped,
                        variant: variant.mapped,
                    }).then((price) => {
                        // eslint-disable-next-line
                        console.log(`State price: ${price.price}`);

                        this.statePrice = price.price;
                    });
                }
            });
        },

        /**
         * Load customization flow.
         */
        loadCustomizeFlow({ startWithProducts, recipe }) {
            const loadProducts = startWithProducts;

            const controller = new this.options.DriveCustomizer.Controller({
                ...this.defaultControllerOptions(),
                ...loadProducts,
                recipe,
            });

            controller.on('afterInitializeState', () => {
            });

            controller.on('afterInitializeBlueprint', () => {
                const appStore = useAppStore();

                // Clear the name if one was loaded from the recipe.
                controller.updatePersonalization('Personalization', '');
                appStore.setPersonalization('');

                // Load actual product options.
                this.loadColors({
                    controllers: [controller],
                    recipe,
                });
            });

            // Load controller.
            this.loadController(controller);
        },

        /**
         * Continue initializing the controller and configure product colors.
         */
        loadColors({ controllers, recipe }) {
            const appStore = useAppStore();

            let showController = null;

            if (controllers.length > 0) {
                // eslint-disable-next-line
                showController = controllers[0];
            }

            if (showController) {
                this.showController(showController);

                if (recipe) {
                    appStore.enableStep(constants.STEP_SELECT_PROOF);
                    appStore.enableStep(constants.STEP_SELECT_WHISKEY);
                    appStore.enableStep(constants.STEP_SELECT_SPIRE);
                    appStore.enableStep(constants.STEP_SELECT_INFUSION);

                    if (window.location.href.includes('skip-name')) {
                        appStore.setCurrentStep(constants.STEP_REVIEW);
                    } else {
                        appStore.setCurrentStep(constants.STEP_PERSONALIZE);
                    }
                } else {
                    // Set the starting step.
                    appStore.setCurrentStep(constants.STEP_SELECT_WHISKEY);
                }
            }
        },

        /**
         * Reloads controller with a different set of products.
         */
        reloadController(product) {
            return new Promise((resolve) => {
                const reloaded = new this.options.DriveCustomizer.Controller({
                    ...this.defaultControllerOptions(),
                    ...{
                        products: [product],
                    },
                });

                reloaded.on('afterInitializeState', () => {
                });

                reloaded.on('afterInitializeBlueprint', () => {
                    resolve(reloaded);
                });

                this.loadController(reloaded);
            });
        },

        /**
         * Selects a controller to show in the UI.
         */
        showController(controller) {
            this.viewController = controller;
        },

        /**
         * Reloads controllers with the new bundle while attempting to save the current printable configuration.
         */
        selectBundle(product) {
            this.reloadController(product).then((controller) => {
                this.showController(controller);
            });
        },

        resetCustomization() {
            this.viewController.load();
        },

        calculateCustomizationCode(controller) {
            const options = (controller ?? this.viewController).state.map((s) => s.component);

            const optionCodes = options.map((component) => ({
                code: component.custom['oe-code'],
                index: component.custom['oe-code-index'],
            })).filter((code) => code.code?.length > 0);

            optionCodes.sort((a, b) => a.index - b.index);

            return optionCodes.map((x) => x.code).join('');
        },

        /**
         * Creates a recipe and adds a product into Shopify cart.
         */
        saveRecipe({ quantity }) {
            this.globalBusy = true;

            this.viewController.saveRecipe({
                translateRecipeAfter: (recipe) => {
                    const customizationCode = this.calculateCustomizationCode();

                    recipe.custom['oe-customization'] = customizationCode;
                    recipe.custom['oe-sku'] = customizationCode;
                    recipe.custom['oe-shopify-product'] = customizationCode;
                    recipe.custom['oe-shopify-variant'] = customizationCode;

                    // eslint-disable-next-line
                    console.log(recipe.custom);
                },
            }).then((recipe) => {
                // Keep the animation going while Shopify adds to cart.
                // this.globalBusy = false;

                // Send the recipe data up to the host.
                const result = this.options.onDone(recipe, quantity);

                if (result) {
                    result.then((shouldClose) => {
                        if (shouldClose) {
                            this.selfDestruct = true;
                        }
                    });
                }
            });
        },

        /**
         * Sets the image displayed by the ProductRenderer component.
         */
        setCurrentImage() {
            const appStore = useAppStore();
            const mainView = this.viewController.blueprint.views.find((view) => view.name === 'Main');
            const previewView = this.viewController.blueprint.views.find((view) => view.name === 'Preview');
            const isReviewStep = appStore.currentStep === constants.STEP_REVIEW;
            const isPersonalizeStep = appStore.currentStep === constants.STEP_PERSONALIZE;
            const selectedInfusion = this.viewController.selectedAtPlacement('Infusion');
            const isPineapple = selectedInfusion.code.includes('Pineapple');
            const showUprightPineapple = (isPineapple && appStore.isMobile) || (isPineapple && (isReviewStep || isPersonalizeStep));
            const view = showUprightPineapple ? previewView : mainView;
            const newImage = this.viewController.makeFrameUrl(view, this.viewController.state, view.frameLow);
            const encodedImage = newImage.replace(/ /g, '%20').replace(/,/g, '%2C');

            this.currentImage = encodedImage;
        },

        setBottleLabelText() {
            this.setWhiskeyLabel();
            this.setProofLabels();
            this.setSpireLabel();
            this.setInfusionLabel();
            this.setFireLabel();
            this.setAlcoholLabel();
            this.setCaptionLabel();
            this.setDescription1Label();
            this.setDescription2Label();
            this.setBarcode();
        },

        setWhiskeyLabel() {
            const component = this.viewController.selectedAtPlacement('Whiskey');
            const whiskeyName = component.custom['oe-label'];

            this.viewController.updatePersonalization('LabelWhiskey', whiskeyName);
        },

        setProofLabels() {
            const component = this.viewController.selectedAtPlacement('Proof');
            const proofName = component.custom['oe-label'];

            this.viewController.updatePersonalization('LabelProof', proofName);
            this.viewController.updatePersonalization('LabelProofTopLeft', proofName);
            this.viewController.updatePersonalization('LabelProofTopRight', proofName);
        },

        setSpireLabel() {
            const component = this.viewController.selectedAtPlacement('Spire');
            const spireName = component.custom['oe-label-wood'];

            this.viewController.updatePersonalization('LabelSpire', spireName);
        },

        setInfusionLabel() {
            const component = this.viewController.selectedAtPlacement('Infusion');
            const infusionName = component.custom['oe-label'];

            this.viewController.updatePersonalization('LabelInfusion', infusionName);
        },

        setFireLabel() {
            const component = this.viewController.selectedAtPlacement('Spire');
            const fireName = component.custom['oe-label-fire'];

            this.viewController.updatePersonalization('LabelFire', fireName);
        },

        setAlcoholLabel() {
            const component = this.viewController.selectedAtPlacement('Proof');
            const abvValue = component.custom['oe-label-abv'];

            if (abvValue) {
                this.viewController.updatePersonalization('LabelAlcohol', `${abvValue}% Alc/Vol`);
            }
        },

        setCaptionLabel() {
            const whiskeyComponent = this.viewController.selectedAtPlacement('Whiskey');
            const whiskeyName = whiskeyComponent.custom['oe-label'];
            const infusion = this.viewController.selectedAtPlacement('Infusion');
            const infusionName = infusion.custom['oe-label'];
            const spireComponent = this.viewController.selectedAtPlacement('Spire');
            const spireName = spireComponent.custom['oe-label'];
            let caption = whiskeyName;

            if (!infusion.placeholder && infusionName) {
                caption = `${whiskeyName} & ${infusionName}`;
            } else if (spireName) {
                caption = `${whiskeyName} & Spire`;
            }

            this.viewController.updatePersonalization('LabelCaption', caption);
        },

        setDescription1Label() {
            const whiskeyComponent = this.viewController.selectedAtPlacement('Whiskey');
            const whiskeyName = whiskeyComponent.custom['oe-label-description-1'];
            const labelCopy = `${whiskeyName} finished with`.toUpperCase();
            const spireComponent = this.viewController.selectedAtPlacement('Spire');
            const spireName = spireComponent.custom['oe-label'];

            if (spireName) {
                this.viewController.updatePersonalization('LabelDescription1', labelCopy);
            }
        },

        setDescription2LabelUsing(useName, target) {
            const appStore = useAppStore();
            const infusionName = useName;
            const spireComponent = this.viewController.selectedAtPlacement('Spire');

            if (!spireComponent.placeholder) {
                const spireName = infusionName ? spireComponent.custom['oe-label'] : spireComponent.description[0].name;
                const article = infusionName ? appStore.getArticle(infusionName) : appStore.getArticle(spireName);
                const infusionCopy = infusionName ? `${infusionName} soaked` : '';
                const labelCopy = `${article} ${infusionCopy} ${spireName} spiral`.toLocaleUpperCase();

                this.viewController.updatePersonalization(target, labelCopy);
            }
        },

        setDescription2Label() {
            const infusionComponent = this.viewController.selectedAtPlacement('Infusion');
            const infusionName = infusionComponent.custom['oe-label-description-1'];
            const infusionShortName = infusionComponent.custom['oe-label-description-short-1'];

            this.setDescription2LabelUsing(infusionName, 'LabelDescription2');
            this.setDescription2LabelUsing(infusionShortName ?? infusionName, 'LabelDescriptionShort2');
        },

        setBarcode() {
            const customizationCode = this.calculateCustomizationCode();

            // Find the barcode component.
            const barcodes = this.viewController.findPlacement('Barcode');
            const barcode = barcodes.components.find((component) => this.viewController.isAvailable(component)
                && component.placementIndependentCode === customizationCode);

            if (barcode) {
                this.viewController.updateComponent(barcodes.code, barcode.code);
            }
        },

        setPersonalizationCheckTimer(timer) {
            this.personalizationTimer = timer;
        },

        getPersonalizationTimerId() {
            this.personalizationTimerId += 1;

            return this.personalizationTimerId;
        },
    },
});
